import {
  GoogleGenAI,
  FunctionCallingConfigMode,
  createPartFromBase64,
  createPartFromFunctionResponse,
  RawReferenceImage,
} from '@google/genai/web';
import type { Model as GoogleModel, Part } from '@google/genai/web';
import type {
  InspectedRequest,
  LLMProviderInterface as Provider,
  ModelCapabilities,
  ProviderFactoryConfig,
  ProviderFinishReason,
  ProviderGeneratedImage,
  ProviderMessage,
  ProviderModelInfo,
  ProviderReasoningDetail,
  ProviderRequest,
  ProviderResponse,
  ProviderStreamChunk,
  ProviderTool,
  ProviderToolCallPart,
  ProviderUsage,
} from '@standardagents/spec';
import { ProviderError } from '@standardagents/spec';
import { getGoogleIconDataUri } from './icons';
import { calculateGoogleUsageCost } from './pricing';

interface GoogleStaticModelEntry {
  id: string;
  name: string;
  description: string;
  capabilities: ModelCapabilities;
}

const STATIC_MODELS: GoogleStaticModelEntry[] = [
  {
    id: 'gemma-4-31b-it',
    name: 'Gemma 4 31B IT',
    description: 'Hosted Gemma 4 instruction-tuned model with image input and optional thinking.',
    capabilities: {
      supportsImages: true,
      supportsToolCalls: false,
      supportsStreaming: true,
      supportsJsonMode: false,
      maxContextTokens: 262144,
      reasoningLevels: { 0: null, 100: 'high' },
    },
  },
  {
    id: 'gemini-2.5-pro',
    name: 'Gemini 2.5 Pro',
    description: 'State-of-the-art Gemini model for coding and reasoning.',
    capabilities: {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: true,
      maxContextTokens: 1048576,
      reasoningLevels: { 0: null, 50: 'medium', 100: 'high' },
    },
  },
  {
    id: 'gemini-2.5-flash',
    name: 'Gemini 2.5 Flash',
    description: 'Fast multimodal Gemini model for general-purpose tasks.',
    capabilities: {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: true,
      maxContextTokens: 1048576,
    },
  },
  {
    id: 'gemini-2.5-flash-lite',
    name: 'Gemini 2.5 Flash-Lite',
    description: 'Low-cost Gemini model for high-volume workloads.',
    capabilities: {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: true,
      maxContextTokens: 1048576,
    },
  },
  {
    id: 'gemini-2.5-flash-image',
    name: 'Gemini 2.5 Flash Image',
    description: 'Gemini image generation model with native image output.',
    capabilities: {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: false,
      maxContextTokens: 1048576,
    },
  },
  {
    id: 'gemini-3-pro-image-preview',
    name: 'Gemini 3 Pro Image Preview',
    description: 'High-end Gemini image-preview model with image generation output.',
    capabilities: {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: false,
      maxContextTokens: 1048576,
    },
  },
  {
    id: 'gemini-3.1-flash-image-preview',
    name: 'Gemini 3.1 Flash Image Preview',
    description: 'Fast Gemini image-preview model with image output.',
    capabilities: {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: false,
      maxContextTokens: 1048576,
    },
  },
  {
    id: 'imagen-4.0-generate-001',
    name: 'Imagen 4 Generate',
    description: 'Google Imagen model for image generation.',
    capabilities: {
      supportsImages: true,
      supportsToolCalls: false,
      supportsStreaming: false,
      supportsJsonMode: false,
      maxContextTokens: 0,
    },
  },
  {
    id: 'imagen-3.0-capability-001',
    name: 'Imagen 3 Capability',
    description: 'Google Imagen model for image editing.',
    capabilities: {
      supportsImages: true,
      supportsToolCalls: false,
      supportsStreaming: false,
      supportsJsonMode: false,
      maxContextTokens: 0,
    },
  },
];

function normalizeModelId(modelId: string): string {
  return modelId
    .trim()
    .replace(/^google\//, '')
    .replace(/^models\//, '')
    .replace(/^publishers\/google\/models\//, '');
}

function googleRestBaseUrl(baseUrl: string | undefined): string {
  const normalized = (baseUrl || 'https://generativelanguage.googleapis.com/v1beta').replace(/\/$/, '');
  if (/\/v1(?:beta|alpha)?$/.test(normalized)) return normalized;
  return `${normalized}/v1beta`;
}

function supportsGoogleMultimodalFunctionResponses(modelId: string): boolean {
  return normalizeModelId(modelId).startsWith('gemini-3');
}

function supportsGoogleVisionInput(modelId: string): boolean {
  const normalizedId = normalizeModelId(modelId);
  return normalizedId.startsWith('gemini-')
    || normalizedId.startsWith('gemma-3')
    || normalizedId.startsWith('gemma-4');
}

function isImagenModel(modelId: string): boolean {
  return normalizeModelId(modelId).startsWith('imagen-');
}

function supportsGoogleVision(model: GoogleModel, normalizedId: string): boolean {
  const description = (model.description || '').toLowerCase();

  if (isImagenModel(normalizedId)) {
    return true;
  }

  if (normalizedId.startsWith('gemini-')) {
    return true;
  }

  if (normalizedId.startsWith('gemma-3') || normalizedId.startsWith('gemma-4')) {
    return true;
  }

  return ['multimodal', 'image', 'vision', 'video', 'audio'].some((term) => description.includes(term));
}

function supportsGoogleToolCalls(normalizedId: string): boolean {
  return normalizedId.startsWith('gemini-') || normalizedId.startsWith('functiongemma');
}

function inferGoogleReasoningLevels(model: GoogleModel, normalizedId: string): Record<number, string | null> | undefined {
  if (!model.thinking) return undefined;

  if (normalizedId.startsWith('gemma-')) {
    return { 0: null, 100: 'high' };
  }

  return { 0: null, 50: 'medium', 100: 'high' };
}

function mapDynamicCapabilities(model: GoogleModel): ModelCapabilities {
  const normalizedId = normalizeModelId(model.name || model.displayName || '');
  const supportedActions = new Set(model.supportedActions || []);
  const supportsContentGeneration = supportedActions.has('generateContent') || normalizedId.startsWith('gemini-') || normalizedId.startsWith('gemma-');
  const supportsTools = supportsGoogleToolCalls(normalizedId);

  return {
    supportsImages: supportsGoogleVision(model, normalizedId),
    supportsToolCalls: supportsTools,
    supportsStreaming: supportsContentGeneration && !isImagenModel(normalizedId),
    supportsJsonMode: supportsTools,
    maxContextTokens: model.inputTokenLimit,
    maxOutputTokens: model.outputTokenLimit,
    reasoningLevels: inferGoogleReasoningLevels(model, normalizedId),
  };
}

function toProviderModelInfo(model: GoogleModel): ProviderModelInfo {
  const id = normalizeModelId(model.name || model.displayName || '');
  return {
    id,
    name: model.displayName || id,
    description: model.description,
    contextLength: model.inputTokenLimit,
    iconId: getGoogleIconDataUri(),
    slug: id,
  };
}

function mapFinishReason(value: string | undefined): ProviderFinishReason {
  switch (value) {
    case 'MAX_TOKENS':
      return 'length';
    case 'SAFETY':
    case 'IMAGE_SAFETY':
    case 'PROHIBITED_CONTENT':
    case 'IMAGE_PROHIBITED_CONTENT':
      return 'content_filter';
    case 'MALFORMED_FUNCTION_CALL':
    case 'UNEXPECTED_TOOL_CALL':
      return 'error';
    case 'STOP':
    default:
      return 'stop';
  }
}

function toolChoiceToGoogle(choice: ProviderRequest['toolChoice']): { mode: FunctionCallingConfigMode; allowedFunctionNames?: string[] } | undefined {
  if (choice === 'auto') {
    return { mode: FunctionCallingConfigMode.AUTO };
  }
  if (choice === 'none') {
    return { mode: FunctionCallingConfigMode.NONE };
  }
  if (choice === 'required') {
    return { mode: FunctionCallingConfigMode.ANY };
  }
  if (choice && typeof choice === 'object' && 'name' in choice) {
    return {
      mode: FunctionCallingConfigMode.ANY,
      allowedFunctionNames: [choice.name],
    };
  }
  return undefined;
}

function transformTools(tools: ProviderTool[] | undefined): any[] | undefined {
  if (!tools?.length) return undefined;

  return [{
    functionDeclarations: tools.map((tool) => ({
      name: tool.function.name,
      description: tool.function.description,
      parameters: tool.function.parameters || {
        type: 'object',
        properties: {},
        required: [],
      },
    })),
  }];
}

function normalizeInlineBytes(
  data: string | undefined,
  mediaType: string | undefined,
): { data: string; mediaType?: string } {
  const value = data || '';
  const match = value.match(/^data:([^;]+);base64,(.*)$/);
  if (!match) {
    return { data: value, mediaType };
  }

  return {
    data: match[2],
    mediaType: match[1] || mediaType,
  };
}

function buildUserParts(message: Extract<ProviderMessage, { role: 'user' }>): Part[] {
  if (typeof message.content === 'string') {
    return [{ text: message.content }];
  }

  return message.content.map((part: any) => {
    if (part.type === 'text') {
      return { text: part.text };
    }

    if (part.type === 'image') {
      const normalized = normalizeInlineBytes(part.data, part.mediaType || 'image/png');
      return createPartFromBase64(normalized.data, normalized.mediaType || 'image/png');
    }

    if (part.type === 'image_url') {
      const url = part.image_url?.url || '';
      if (url.startsWith('data:')) {
        const match = url.match(/^data:([^;]+);base64,(.*)$/);
        if (match) {
          return createPartFromBase64(match[2], match[1]);
        }
      }
      return {
        fileData: {
          mimeType: 'image/*',
          fileUri: url,
        },
      };
    }

    return {
      inlineData: {
        mimeType: part.mediaType,
        data: part.data,
      },
    };
  });
}

function buildAssistantParts(message: Extract<ProviderMessage, { role: 'assistant' }>): Part[] {
  const parts: Part[] = [];

  if (message.content) {
    parts.push({ text: message.content });
  }

  const reasoningParts = buildGoogleReasoningParts(message.reasoning, message.reasoningDetails);
  if (reasoningParts.length) {
    parts.push(...reasoningParts);
  }

  if (message.toolCalls?.length) {
    for (const toolCall of message.toolCalls) {
      const thoughtSignature = getGoogleThoughtSignature(
        toolCall.extraContent,
        message.reasoningDetails,
        toolCall.id
      );
      parts.push({
        functionCall: {
          id: toolCall.id,
          name: toolCall.name,
          args: toolCall.arguments,
        },
        ...(thoughtSignature ? { thoughtSignature } : {}),
      });
    }
  }

  return parts;
}

function buildToolParts(
  message: Extract<ProviderMessage, { role: 'tool' }>,
  modelId: string
): Part[] {
  const response: Record<string, unknown> = {};

  if (typeof message.content === 'string') {
    response.output = message.content;
  } else if ('type' in message.content) {
    if (message.content.type === 'text') response.output = message.content.text;
    else if (message.content.type === 'error') response.error = message.content.error;
    else response.output = message.content;
  } else {
    response.output = message.content;
  }

  const imageParts = message.attachments
    ?.filter((attachment: any) => attachment.type === 'image' && attachment.data)
    .map((attachment: any) => {
      const normalized = normalizeInlineBytes(attachment.data, attachment.mediaType);
      return createPartFromBase64(normalized.data, normalized.mediaType || 'image/png');
    }) || [];

  if (supportsGoogleMultimodalFunctionResponses(modelId)) {
    return [
      createPartFromFunctionResponse(
        message.toolCallId,
        message.toolName,
        response,
        imageParts.length ? imageParts : undefined
      ),
    ];
  }

  const parts: Part[] = [
    createPartFromFunctionResponse(
      message.toolCallId,
      message.toolName,
      response
    ),
  ];

  if (supportsGoogleVisionInput(modelId) && imageParts.length) {
    parts.push(...imageParts);
  }

  return parts;
}

function buildContents(messages: ProviderMessage[], modelId: string): { systemInstruction?: any; contents: any[] } {
  const contents: any[] = [];
  const systemMessages = messages.filter((message) => message.role === 'system').map((message) => message.content);
  const systemInstruction = systemMessages.length
    ? { role: 'user', parts: [{ text: systemMessages.join('\n\n') }] }
    : undefined;

  for (const message of messages) {
    if (message.role === 'system') continue;
    if (message.role === 'user') {
      contents.push({ role: 'user', parts: buildUserParts(message) });
      continue;
    }
    if (message.role === 'assistant') {
      contents.push({ role: 'model', parts: buildAssistantParts(message) });
      continue;
    }
    contents.push({ role: 'user', parts: buildToolParts(message, modelId) });
  }

  return { systemInstruction, contents };
}

function extractReasoningFromParts(parts: Part[] | undefined): string | null {
  if (!parts?.length) return null;
  const text = parts
    .filter((part: any) => part.thought && typeof part.text === 'string')
    .map((part: any) => part.text)
    .join('');
  return text || null;
}

function extractGoogleReasoningDetails(parts: Part[] | undefined): ProviderReasoningDetail[] | undefined {
  if (!parts?.length) return undefined;

  const reasoningDetails: ProviderReasoningDetail[] = [];

  for (const part of parts) {
    if (!part.thought) continue;

    if (typeof part.text === 'string' && part.text.length > 0) {
      reasoningDetails.push({
        type: 'text',
        text: part.text,
        format: 'google-gemini-v1',
      });
    }

    if (typeof part.thoughtSignature === 'string' && part.thoughtSignature.length > 0) {
      reasoningDetails.push({
        type: 'encrypted',
        data: part.thoughtSignature,
        format: 'google-gemini-thought-signature-v1',
      });
    }
  }

  return reasoningDetails.length ? reasoningDetails : undefined;
}

function extractImagesFromParts(parts: Part[] | undefined): ProviderGeneratedImage[] | undefined {
  if (!parts?.length) return undefined;
  const images = parts
    .filter((part: any) => part.inlineData?.mimeType?.startsWith('image/'))
    .map((part: any, index: number) => ({
      id: `image_${index}`,
      data: part.inlineData?.data || '',
      mediaType: part.inlineData?.mimeType || 'image/png',
    }));
  return images.length ? images : undefined;
}

function extractToolCalls(parts: Part[] | undefined): ProviderToolCallPart[] | undefined {
  if (!parts?.length) return undefined;
  const toolCalls = parts
    .filter((part: any) => part.functionCall?.name)
    .map((part: any) => ({
      id: part.functionCall?.id || crypto.randomUUID(),
      name: part.functionCall?.name || 'tool',
      arguments: (part.functionCall?.args || {}) as Record<string, unknown>,
      extraContent: getGoogleToolCallExtraContent(part),
    }));

  return toolCalls.length ? toolCalls : undefined;
}

function getGoogleThoughtSignature(
  extraContent: unknown,
  reasoningDetails?: ProviderReasoningDetail[],
  toolCallId?: string
): string | undefined {
  if (extraContent && typeof extraContent === 'object') {
    const google = (extraContent as Record<string, unknown>).google;
    if (google && typeof google === 'object') {
      const thoughtSignature = (google as Record<string, unknown>).thought_signature;
      if (typeof thoughtSignature === 'string' && thoughtSignature.length > 0) {
        return thoughtSignature;
      }
    }
  }

  if (!toolCallId || !reasoningDetails?.length) return undefined;

  const detail = reasoningDetails.find((candidate) => (
    candidate?.type === 'encrypted' &&
    candidate?.id === toolCallId &&
    typeof candidate?.data === 'string' &&
    candidate.data.length > 0 &&
    (
      candidate.format === 'google-gemini-v1' ||
      candidate.format === 'google-gemini-thought-signature-v1' ||
      !candidate.format
    )
  ));

  return detail?.data;
}

function getGoogleToolCallExtraContent(part: Part): Record<string, unknown> | undefined {
  const thoughtSignature = typeof part.thoughtSignature === 'string' && part.thoughtSignature.length > 0
    ? part.thoughtSignature
    : undefined;

  if (!thoughtSignature) return undefined;

  return {
    google: {
      thought_signature: thoughtSignature,
    },
  };
}

function buildGoogleReasoningParts(
  reasoning: string | null | undefined,
  reasoningDetails: ProviderReasoningDetail[] | undefined
): Part[] {
  const googleReasoningDetails = reasoningDetails?.filter(
    (detail) =>
      detail?.format === 'google-gemini-v1' ||
      detail?.format === 'google-gemini-thought-signature-v1'
  ) || [];

  if (googleReasoningDetails.length === 0) {
    return reasoning ? [{ text: reasoning, thought: true }] : [];
  }

  const parts: Part[] = [];

  for (const detail of googleReasoningDetails) {
    if (detail.format === 'google-gemini-v1' && typeof detail.text === 'string') {
      parts.push({ text: detail.text, thought: true });
      continue;
    }

    if (detail.format === 'google-gemini-thought-signature-v1' && typeof detail.data === 'string') {
      const lastThoughtPart = parts.length > 0 ? parts[parts.length - 1] : undefined;
      if (lastThoughtPart?.thought && !lastThoughtPart.thoughtSignature) {
        lastThoughtPart.thoughtSignature = detail.data;
      } else {
        parts.push({ thought: true, thoughtSignature: detail.data });
      }
    }
  }

  if (parts.length === 0 && reasoning) {
    parts.push({ text: reasoning, thought: true });
  }

  return parts;
}

function transformUsage(modelId: string, usage: any, imageCount = 0): ProviderUsage {
  const mapped: ProviderUsage = {
    promptTokens: usage?.promptTokenCount || 0,
    completionTokens: usage?.candidatesTokenCount || 0,
    totalTokens: usage?.totalTokenCount || 0,
    reasoningTokens: usage?.thoughtsTokenCount,
    cachedTokens: usage?.cachedContentTokenCount,
    provider: 'google',
  };

  mapped.cost = calculateGoogleUsageCost(
    modelId,
    {
      ...mapped,
      promptTokenDetails: usage?.promptTokensDetails,
      completionTokenDetails: usage?.candidatesTokensDetails || usage?.responseTokensDetails,
      cachedTokenDetails: usage?.cacheTokensDetails,
    },
    imageCount
  );
  return mapped;
}

export class GoogleProvider implements Provider {
  readonly name = 'google';
  readonly specificationVersion = '1' as const;

  private readonly config: ProviderFactoryConfig;
  private client: GoogleGenAI | null = null;

  private static readonly cacheTtl = 5 * 60 * 1000;
  private static modelsCache: ProviderModelInfo[] | null = null;
  private static modelsCacheTime = 0;
  private static modelMetadataCache = new Map<string, GoogleModel>();

  constructor(config: ProviderFactoryConfig) {
    this.config = config;
  }

  private getClient(): GoogleGenAI {
    if (!this.client) {
      this.client = new GoogleGenAI({
        apiKey: this.config.apiKey,
      });
    }
    return this.client;
  }

  supportsModel(modelId: string): boolean {
    return modelId.startsWith('gemini-')
      || modelId.startsWith('gemma-')
      || modelId.startsWith('imagen-')
      || modelId.startsWith('models/');
  }

  getIcon(_modelId?: string): string {
    return getGoogleIconDataUri();
  }

  private getHttpOptions(): Record<string, unknown> | undefined {
    if (!this.config.baseUrl && !this.config.timeout) return undefined;

    return {
      ...(this.config.baseUrl ? { baseUrl: this.config.baseUrl } : {}),
      ...(this.config.timeout ? { timeout: this.config.timeout } : {}),
    };
  }

  async getModels(filter?: string): Promise<ProviderModelInfo[]> {
    const now = Date.now();
    if (GoogleProvider.modelsCache && now - GoogleProvider.modelsCacheTime < GoogleProvider.cacheTtl) {
      return this.filterModels(GoogleProvider.modelsCache, filter);
    }

    try {
      const models: ProviderModelInfo[] = [];
      const client = this.getClient();
      const pager = await client.models.list({
        config: {
          httpOptions: this.getHttpOptions() as any,
        },
      } as any);

      for await (const model of pager as AsyncIterable<GoogleModel>) {
        const id = normalizeModelId(model.name || '');
        if (!id) continue;
        const supportsGeneration = model.supportedActions?.some((action: string) =>
          ['generateContent', 'generateImages', 'editImage', 'predict'].includes(action)
        );
        if (!supportsGeneration) continue;
        GoogleProvider.modelMetadataCache.set(id, model);
        models.push(toProviderModelInfo(model));
      }

      for (const staticModel of STATIC_MODELS) {
        if (!models.some((model) => model.id === staticModel.id)) {
          models.push({
            id: staticModel.id,
            name: staticModel.name,
            description: staticModel.description,
            iconId: this.getIcon(staticModel.id),
            slug: staticModel.id,
          });
        }
      }

      GoogleProvider.modelsCache = models.sort((a, b) => a.name.localeCompare(b.name));
      GoogleProvider.modelsCacheTime = now;
      return this.filterModels(GoogleProvider.modelsCache, filter);
    } catch (error) {
      console.error('Failed to list Google models:', error);
      return this.filterModels(
        STATIC_MODELS.map((model) => ({
          id: model.id,
          name: model.name,
          description: model.description,
          iconId: this.getIcon(model.id),
          slug: model.id,
        })),
        filter
      );
    }
  }

  private filterModels(models: ProviderModelInfo[], filter?: string): ProviderModelInfo[] {
    if (!filter) return models;
    const query = filter.toLowerCase();
    return models.filter((model) =>
      model.id.toLowerCase().includes(query) ||
      model.name.toLowerCase().includes(query) ||
      (model.description?.toLowerCase().includes(query) ?? false)
    );
  }

  private async getModelMetadata(modelId: string): Promise<GoogleModel | null> {
    const normalized = normalizeModelId(modelId);
    const cached = GoogleProvider.modelMetadataCache.get(normalized);
    if (cached) {
      return cached;
    }

    try {
      const model = await this.getClient().models.get({
        model: normalized,
        config: {
          httpOptions: this.getHttpOptions() as any,
        },
      } as any);
      GoogleProvider.modelMetadataCache.set(normalized, model);
      return model;
    } catch {
      return null;
    }
  }

  async getModelCapabilities(modelId: string): Promise<ModelCapabilities | null> {
    const normalized = normalizeModelId(modelId);
    const staticModel = STATIC_MODELS.find((model) => model.id === normalized);
    if (staticModel) {
      return { ...staticModel.capabilities };
    }

    try {
      const metadata = await this.getModelMetadata(normalized);
      if (metadata) {
        return mapDynamicCapabilities(metadata);
      }

      const models = await this.getModels();
      const found = models.find((model) => model.id === normalized);
      if (!found) return null;

      return {
        supportsImages: normalized.startsWith('gemini-') || normalized.startsWith('gemma-') || normalized.includes('image'),
        supportsToolCalls: supportsGoogleToolCalls(normalized),
        supportsStreaming: !isImagenModel(normalized),
        supportsJsonMode: supportsGoogleToolCalls(normalized),
        maxContextTokens: found.contextLength,
      };
    } catch {
      return null;
    }
  }

  private buildGenerateContentParams(request: ProviderRequest): Record<string, unknown> {
    const providerOptions = (request.providerOptions || {}) as Record<string, unknown>;
    const { systemInstruction, contents } = buildContents(request.messages, request.model);

    const config: Record<string, unknown> = {
      ...(this.getHttpOptions() ? { httpOptions: this.getHttpOptions() } : {}),
      ...(systemInstruction ? { systemInstruction } : {}),
      ...(typeof request.temperature === 'number' ? { temperature: request.temperature } : {}),
      ...(typeof request.topP === 'number' ? { topP: request.topP } : {}),
      ...(typeof request.topK === 'number' ? { topK: request.topK } : {}),
      ...(typeof request.maxOutputTokens === 'number' ? { maxOutputTokens: request.maxOutputTokens } : {}),
      ...(request.stopSequences?.length ? { stopSequences: request.stopSequences } : {}),
      ...(request.responseFormat?.type === 'json'
        ? {
            responseMimeType: 'application/json',
            ...(request.responseFormat.schema ? { responseJsonSchema: request.responseFormat.schema } : {}),
          }
        : {}),
      ...(request.tools?.length ? { tools: transformTools(request.tools) } : {}),
      ...(request.tools?.length && toolChoiceToGoogle(request.toolChoice)
        ? { toolConfig: { functionCallingConfig: toolChoiceToGoogle(request.toolChoice) } }
        : {}),
      ...(typeof providerOptions.candidateCount === 'number' ? { candidateCount: providerOptions.candidateCount } : {}),
      ...(Array.isArray(providerOptions.responseModalities) ? { responseModalities: providerOptions.responseModalities } : {}),
      ...(typeof providerOptions.mediaResolution === 'string' ? { mediaResolution: providerOptions.mediaResolution } : {}),
      ...(typeof providerOptions.cachedContent === 'string' ? { cachedContent: providerOptions.cachedContent } : {}),
      ...(typeof providerOptions.enableEnhancedCivicAnswers === 'boolean'
        ? { enableEnhancedCivicAnswers: providerOptions.enableEnhancedCivicAnswers }
        : {}),
      ...(providerOptions.labels ? { labels: providerOptions.labels } : {}),
      ...(providerOptions.safetySettings ? { safetySettings: providerOptions.safetySettings } : {}),
      ...(providerOptions.thinkingConfig ? { thinkingConfig: providerOptions.thinkingConfig } : {}),
      ...(providerOptions.imageConfig ? { imageConfig: providerOptions.imageConfig } : {}),
      ...(typeof request.reasoning?.maxTokens === 'number'
        ? { thinkingConfig: { ...(providerOptions.thinkingConfig as Record<string, unknown> || {}), thinkingBudget: request.reasoning.maxTokens } }
        : {}),
    };

    return {
      model: normalizeModelId(request.model),
      contents,
      config,
    };
  }

  private extractImagenRequest(request: ProviderRequest): {
    prompt: string;
    referenceImages?: RawReferenceImage[];
    body: Record<string, unknown>;
  } {
    const providerOptions = (request.providerOptions || {}) as Record<string, unknown>;
    const latestUser = [...request.messages].reverse().find((message) => message.role === 'user');
    const prompt = latestUser ? (typeof latestUser.content === 'string'
      ? latestUser.content
      : latestUser.content.filter((part: any) => part.type === 'text').map((part: any) => part.text).join('\n')) : '';

    const referenceImages = latestUser && typeof latestUser.content !== 'string'
      ? latestUser.content
          .filter((part: any) => part.type === 'image')
          .map((part: any, index: number) => {
            const normalized = normalizeInlineBytes(part.data, part.mediaType);
            const image = new RawReferenceImage();
            image.referenceId = index + 1;
            image.referenceImage = {
              imageBytes: normalized.data,
              mimeType: normalized.mediaType,
            };
            return image;
          })
      : [];

    const body: Record<string, unknown> = {
      model: normalizeModelId(request.model),
      prompt,
      ...(referenceImages.length ? { referenceImages: '[binary images omitted]' } : {}),
      config: {
        ...(this.getHttpOptions() ? { httpOptions: this.getHttpOptions() } : {}),
        ...(typeof providerOptions.numberOfImages === 'number' ? { numberOfImages: providerOptions.numberOfImages } : {}),
        ...(typeof providerOptions.negativePrompt === 'string' ? { negativePrompt: providerOptions.negativePrompt } : {}),
        ...(typeof providerOptions.aspectRatio === 'string' ? { aspectRatio: providerOptions.aspectRatio } : {}),
        ...(typeof providerOptions.guidanceScale === 'number' ? { guidanceScale: providerOptions.guidanceScale } : {}),
        ...(typeof providerOptions.seed === 'number' ? { seed: providerOptions.seed } : {}),
        ...(typeof providerOptions.safetyFilterLevel === 'string' ? { safetyFilterLevel: providerOptions.safetyFilterLevel } : {}),
        ...(typeof providerOptions.personGeneration === 'string' ? { personGeneration: providerOptions.personGeneration } : {}),
        ...(typeof providerOptions.includeSafetyAttributes === 'boolean' ? { includeSafetyAttributes: providerOptions.includeSafetyAttributes } : {}),
        ...(typeof providerOptions.includeRaiReason === 'boolean' ? { includeRaiReason: providerOptions.includeRaiReason } : {}),
        ...(typeof providerOptions.language === 'string' ? { language: providerOptions.language } : {}),
        ...(typeof providerOptions.outputMimeType === 'string' ? { outputMimeType: providerOptions.outputMimeType } : {}),
        ...(typeof providerOptions.outputCompressionQuality === 'number' ? { outputCompressionQuality: providerOptions.outputCompressionQuality } : {}),
        ...(typeof providerOptions.addWatermark === 'boolean' ? { addWatermark: providerOptions.addWatermark } : {}),
        ...(providerOptions.labels ? { labels: providerOptions.labels } : {}),
        ...(typeof providerOptions.imageSize === 'string' ? { imageSize: providerOptions.imageSize } : {}),
        ...(typeof providerOptions.enhancePrompt === 'boolean' ? { enhancePrompt: providerOptions.enhancePrompt } : {}),
        ...(typeof providerOptions.editMode === 'string' ? { editMode: providerOptions.editMode } : {}),
        ...(typeof providerOptions.baseSteps === 'number' ? { baseSteps: providerOptions.baseSteps } : {}),
      },
    };

    return {
      prompt,
      referenceImages: referenceImages.length ? referenceImages : undefined,
      body,
    };
  }

  async inspectRequest(request: ProviderRequest): Promise<InspectedRequest> {
    const baseUrl = googleRestBaseUrl(this.config.baseUrl);

    if (isImagenModel(request.model)) {
      const imagen = this.extractImagenRequest(request);
      return {
        body: imagen.body,
        messagesPath: 'prompt',
        metadata: {
          endpoint: imagen.referenceImages?.length
            ? `${this.config.baseUrl || 'https://generativelanguage.googleapis.com'}/editImage`
            : `${this.config.baseUrl || 'https://generativelanguage.googleapis.com'}/generateImages`,
          headers: { 'Content-Type': 'application/json' },
        },
      };
    }

    const { model: _model, ...body } = this.buildGenerateContentParams(request);

    return {
      body,
      messagesPath: 'contents',
      metadata: {
        endpoint: `${baseUrl}/models/${encodeURIComponent(normalizeModelId(request.model))}:generateContent`,
        headers: { 'Content-Type': 'application/json' },
      },
    };
  }

  async generate(request: ProviderRequest): Promise<ProviderResponse> {
    if (isImagenModel(request.model)) {
      return this.generateImagen(request);
    }

    try {
      const client = this.getClient();
      const response = await client.models.generateContent(this.buildGenerateContentParams(request) as any);
      const candidate = response.candidates?.[0];
      const parts = candidate?.content?.parts;
      const images = extractImagesFromParts(parts);
      const usage = transformUsage(normalizeModelId(request.model), response.usageMetadata, images?.length || 0);

      return {
        content: response.text || null,
        reasoning: extractReasoningFromParts(parts),
        reasoningDetails: extractGoogleReasoningDetails(parts),
        toolCalls: extractToolCalls(parts),
        images,
        finishReason: mapFinishReason(candidate?.finishReason),
        usage,
        metadata: {
          provider: 'google',
          responseId: response.responseId,
          modelVersion: response.modelVersion,
          promptFeedback: response.promptFeedback,
        },
      };
    } catch (error) {
      throw this.toProviderError(error);
    }
  }

  private async generateImagen(request: ProviderRequest): Promise<ProviderResponse> {
    try {
      const client = this.getClient();
      const imagen = this.extractImagenRequest(request);
      const model = normalizeModelId(request.model);

      const response = imagen.referenceImages?.length
        ? await client.models.editImage({
            model,
            prompt: imagen.prompt,
            referenceImages: imagen.referenceImages,
            config: imagen.body.config as any,
          })
        : await client.models.generateImages({
            model,
            prompt: imagen.prompt,
            config: imagen.body.config as any,
          });

      const images: ProviderGeneratedImage[] = (response.generatedImages || [])
        .filter((image: any) => image.image?.imageBytes)
        .map((image: any, index: number) => ({
          id: `image_${index}`,
          data: image.image?.imageBytes || '',
          mediaType: image.image?.mimeType || 'image/png',
          revisedPrompt: image.enhancedPrompt,
        }));

      return {
        content: null,
        images,
        finishReason: 'stop',
        usage: {
          promptTokens: 0,
          completionTokens: 0,
          totalTokens: 0,
          cost: calculateGoogleUsageCost(model, { promptTokens: 0, completionTokens: 0, cachedTokens: 0 }, images.length),
          provider: 'google',
        },
        metadata: {
          provider: 'google',
        },
      };
    } catch (error) {
      throw this.toProviderError(error);
    }
  }

  async stream(request: ProviderRequest): Promise<AsyncIterable<ProviderStreamChunk>> {
    if (isImagenModel(request.model)) {
      throw new ProviderError('Imagen models do not support streaming in Standard Agents.', 'invalid_request');
    }

    const self = this;

    try {
      const client = this.getClient();
      const stream = await client.models.generateContentStream(this.buildGenerateContentParams(request) as any);

      return {
        async *[Symbol.asyncIterator]() {
          let sawContent = false;
          let sawReasoning = false;
          const reasoningDetails: ProviderReasoningDetail[] = [];
          let finishReason: ProviderFinishReason = 'stop';
          let finalUsage: ProviderUsage = {
            promptTokens: 0,
            completionTokens: 0,
            totalTokens: 0,
            provider: 'google',
          };
          let imageIndex = 0;

          try {
            for await (const chunk of stream) {
              const candidate = chunk.candidates?.[0];
              const parts = candidate?.content?.parts || [];
              const chunkReasoningDetails = extractGoogleReasoningDetails(parts);
              if (chunkReasoningDetails?.length) {
                reasoningDetails.push(...chunkReasoningDetails);
              }

              for (const part of parts) {
                if (part.text && !part.thought) {
                  sawContent = true;
                  yield { type: 'content-delta', delta: part.text };
                } else if (part.text && part.thought) {
                  sawReasoning = true;
                  yield { type: 'reasoning-delta', delta: part.text };
                } else if (part.functionCall?.name) {
                  const id = part.functionCall.id || crypto.randomUUID();
                  const extraContent = getGoogleToolCallExtraContent(part);
                  yield { type: 'tool-call-start', id, name: part.functionCall.name, extraContent };
                  yield {
                    type: 'tool-call-done',
                    id,
                    arguments: (part.functionCall.args || {}) as Record<string, unknown>,
                    extraContent,
                  };
                } else if (part.inlineData?.mimeType?.startsWith('image/')) {
                  const image: ProviderGeneratedImage = {
                    id: `image_${imageIndex}`,
                    data: part.inlineData.data || '',
                    mediaType: part.inlineData.mimeType || 'image/png',
                  };
                  yield { type: 'image-done', index: imageIndex, image };
                  imageIndex += 1;
                }
              }

              if (chunk.usageMetadata) {
                finalUsage = transformUsage(normalizeModelId(request.model), chunk.usageMetadata, imageIndex);
              }

              if (candidate?.finishReason) {
                finishReason = mapFinishReason(candidate.finishReason);
              }
            }

            if (sawContent) yield { type: 'content-done' };
            if (sawReasoning) yield { type: 'reasoning-done' };
            yield {
              type: 'finish',
              finishReason,
              usage: finalUsage,
              reasoningDetails: reasoningDetails.length ? reasoningDetails : undefined,
            };
          } catch (error) {
            yield { type: 'error', error: self.toProviderError(error).message };
          }
        },
      };
    } catch (error) {
      throw this.toProviderError(error);
    }
  }

  private toProviderError(error: unknown): ProviderError {
    if (error instanceof ProviderError) return error;

    if (error instanceof Error) {
      const candidate = error as Error & { status?: number; statusCode?: number };
      const status = candidate.status || candidate.statusCode;
      if (status === 429) return new ProviderError(error.message, 'rate_limit', status);
      if (status === 401 || status === 403) return new ProviderError(error.message, 'auth_error', status);
      if (status === 400) return new ProviderError(error.message, 'invalid_request', status);
      if (status && status >= 500) return new ProviderError(error.message, 'server_error', status);
      if (error.name === 'AbortError') return new ProviderError(error.message, 'timeout');
      return new ProviderError(error.message, 'unknown', status);
    }

    return new ProviderError(String(error), 'unknown');
  }
}
